import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import APIService from "../APIService";
import FacultyDashboardShell from "./FacultyDashboardShell";
import { getFacultyWorkspaceCourse, setFacultyWorkspaceCourse } from "../utils/facultyWorkspace";
import "./Dashboard.css";
import "./AddCourse.css";

const ALLOWED_TYPES = ["application/pdf", "text/plain"];
const ALLOWED_EXT_LABEL = ".pdf, .txt";
const MAX_FILE_SIZE_MB = 10;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

const EMPTY_ASSIGNMENT_FORM = {
  title: "",
  description: "",
  assignmentType: "assignment",
  assignmentNumber: "",
  isActive: true,
};

function AddCourse() {
  const navigate = useNavigate();
  const generalFileInputRef = useRef(null);
  const assignmentFileInputRef = useRef(null);
  const replaceFileInputRef = useRef(null);

  const [authorized, setAuthorized] = useState(false);
  const [checkingAuth, setCheckingAuth] = useState(true);
  const [courses, setCourses] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [selectedCourseId, setSelectedCourseId] = useState(() => getFacultyWorkspaceCourse()?.courseId || "");
  const [selectedAssignmentId, setSelectedAssignmentId] = useState("");
  const [generalResources, setGeneralResources] = useState([]);
  const [assignmentResources, setAssignmentResources] = useState([]);
  const [generalDocumentType, setGeneralDocumentType] = useState("syllabus");
  const [assignmentDocumentType, setAssignmentDocumentType] = useState("assignment");
  const [generalFiles, setGeneralFiles] = useState([]);
  const [assignmentFiles, setAssignmentFiles] = useState([]);
  const [validationErrors, setValidationErrors] = useState([]);
  const [uploadError, setUploadError] = useState("");
  const [uploadMessage, setUploadMessage] = useState("");
  const [uploadingScope, setUploadingScope] = useState("");
  const [replacingResource, setReplacingResource] = useState(null);
  const [previewResourceId, setPreviewResourceId] = useState(null);
  const [editingAssignmentId, setEditingAssignmentId] = useState(null);
  const [assignmentForm, setAssignmentForm] = useState(EMPTY_ASSIGNMENT_FORM);
  const [renamingResourceId, setRenamingResourceId] = useState(null);
  const [renameValue, setRenameValue] = useState("");

  const selectedCourse = useMemo(
    () => courses.find((course) => course.courseId === selectedCourseId) || null,
    [courses, selectedCourseId]
  );
  const selectedAssignment = useMemo(
    () => assignments.find((assignment) => String(assignment.assignmentId) === String(selectedAssignmentId)) || null,
    [assignments, selectedAssignmentId]
  );
  const previewResource = useMemo(() => {
    const allResources = [...generalResources, ...assignmentResources];
    return allResources.find((resource) => resource.resourceId === previewResourceId) || null;
  }, [assignmentResources, generalResources, previewResourceId]);

  const loadCourses = useCallback(async (preferredCourseId = null) => {
    const data = await APIService.GetCourses(false, true);
    const normalized = Array.isArray(data) ? data : [];
    setCourses(normalized);
    const nextCourseId =
      preferredCourseId ||
      (normalized.some((course) => course.courseId === selectedCourseId) ? selectedCourseId : normalized[0]?.courseId) ||
      "";
    setSelectedCourseId(nextCourseId);
  }, [selectedCourseId]);

  const loadAssignments = useCallback(async (courseId) => {
    if (!courseId) {
      setAssignments([]);
      setSelectedAssignmentId("");
      return;
    }

    const data = await APIService.GetAssignments(courseId, true);
    const activeAssignments = Array.isArray(data) ? data.filter((assignment) => assignment.isActive) : [];
    setAssignments(activeAssignments);
    if (!activeAssignments.some((assignment) => String(assignment.assignmentId) === String(selectedAssignmentId))) {
      setSelectedAssignmentId("");
    }
  }, [selectedAssignmentId]);

  const loadResources = useCallback(async (courseId, assignmentId) => {
    if (!courseId) {
      setGeneralResources([]);
      setAssignmentResources([]);
      return;
    }

    const [generalData, assignmentData] = await Promise.all([
      APIService.GetCourseResources(courseId, "general", false),
      assignmentId ? APIService.GetCourseResources(courseId, assignmentId, false) : Promise.resolve([]),
    ]);

    setGeneralResources(Array.isArray(generalData) ? generalData : []);
    setAssignmentResources(Array.isArray(assignmentData) ? assignmentData : []);
  }, []);

  useEffect(() => {
    let active = true;

    async function bootstrap() {
      try {
        const data = await APIService.GetUserPrimaryAttribute();
        const isManager = Boolean(data?.faculty_access);

        if (!isManager) {
          navigate("/", { replace: true });
          return;
        }

        if (!active) return;
        setAuthorized(true);
        await loadCourses();
      } catch (_error) {
        navigate("/", { replace: true });
      } finally {
        if (active) setCheckingAuth(false);
      }
    }

    bootstrap();
    return () => {
      active = false;
    };
  }, [loadCourses, navigate]);

  useEffect(() => {
    loadAssignments(selectedCourseId).catch((error) => setUploadError(error.message || "Failed to load assignments."));
  }, [loadAssignments, selectedCourseId]);

  useEffect(() => {
    loadResources(selectedCourseId, selectedAssignmentId).catch((error) => setUploadError(error.message || "Failed to load uploaded files."));
  }, [loadResources, selectedAssignmentId, selectedCourseId]);

  useEffect(() => {
    setFacultyWorkspaceCourse(selectedCourse);
  }, [selectedCourse]);

  useEffect(() => {
    if (previewResourceId && ![...generalResources, ...assignmentResources].some((resource) => resource.resourceId === previewResourceId)) {
      setPreviewResourceId(null);
    }
  }, [assignmentResources, generalResources, previewResourceId]);

  function resetFeedback() {
    setUploadError("");
    setUploadMessage("");
  }

  function validateFiles(files) {
    const errors = [];
    Array.from(files).forEach((file) => {
      const ext = file.name.split(".").pop().toLowerCase();
      if (!ALLOWED_TYPES.includes(file.type) && !["pdf", "txt"].includes(ext)) {
        errors.push(`"${file.name}": unsupported type. Only .pdf and .txt are allowed.`);
      } else if (file.size > MAX_FILE_SIZE_BYTES) {
        errors.push(`"${file.name}": ${(file.size / (1024 * 1024)).toFixed(1)} MB exceeds the ${MAX_FILE_SIZE_MB} MB limit.`);
      }
    });
    return errors;
  }

  function handleFileChange(scope, event) {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    const errors = validateFiles(files);
    setValidationErrors(errors);
    resetFeedback();

    if (errors.length > 0) {
      if (scope === "general") {
        setGeneralFiles([]);
        if (generalFileInputRef.current) generalFileInputRef.current.value = "";
      } else {
        setAssignmentFiles([]);
        if (assignmentFileInputRef.current) assignmentFileInputRef.current.value = "";
      }
      return;
    }

    if (scope === "general") {
      setGeneralFiles(Array.from(files));
    } else {
      setAssignmentFiles(Array.from(files));
    }
  }

  async function uploadFiles(scope, filesOverride = null) {
    if (!selectedCourseId) {
      setUploadError("Choose a course first.");
      return;
    }

    const isReplacement = Boolean(replacingResource && filesOverride);
    const effectiveCourseId = isReplacement ? replacingResource.courseId : selectedCourseId;
    const effectiveAssignmentId = isReplacement
      ? (replacingResource.assignmentId || null)
      : (scope === "assignment" ? selectedAssignmentId : null);
    const effectiveDocumentType = isReplacement
      ? (replacingResource.documentType || (scope === "assignment" ? assignmentDocumentType : generalDocumentType))
      : (scope === "assignment" ? assignmentDocumentType : generalDocumentType);
    const isAssignmentScope = Boolean(effectiveAssignmentId);
    const files = filesOverride || (isAssignmentScope ? assignmentFiles : generalFiles);

    if (!effectiveCourseId) {
      setUploadError("Choose a course first.");
      return;
    }
    if (scope === "assignment" && !effectiveAssignmentId) {
      setUploadError("Choose an assignment first.");
      return;
    }
    if (!files.length) {
      setUploadError("Choose at least one file to upload.");
      return;
    }

    setUploadingScope(scope);
    resetFeedback();

    try {
      await APIService.UploadSyllabus(files, {
        courseId: effectiveCourseId,
        assignmentId: effectiveAssignmentId,
        documentType: effectiveDocumentType,
        replacementFilename: replacingResource?.originalFilename || null,
      });

      setUploadMessage(
        replacingResource
          ? `Updated ${replacingResource.originalFilename}.`
          : `Uploaded ${files.length > 1 ? "files" : "file"} to ${isAssignmentScope ? "assignment material" : "course material"}.`
      );

      setReplacingResource(null);
      setValidationErrors([]);
      setGeneralFiles([]);
      setAssignmentFiles([]);
      if (generalFileInputRef.current) generalFileInputRef.current.value = "";
      if (assignmentFileInputRef.current) assignmentFileInputRef.current.value = "";
      if (replaceFileInputRef.current) replaceFileInputRef.current.value = "";

      await loadResources(selectedCourseId, selectedAssignmentId);
    } catch (error) {
      setUploadError(error.message || "Upload failed.");
    } finally {
      setUploadingScope("");
    }
  }

  async function handleDeleteResource(resourceId) {
    resetFeedback();
    try {
      await APIService.DeleteCourseResource(resourceId);
      setUploadMessage("File removed.");
      await loadResources(selectedCourseId, selectedAssignmentId);
    } catch (error) {
      setUploadError(error.message || "Failed to delete file.");
    }
  }

  function handleReplaceIntent(resource) {
    setReplacingResource(resource);
    resetFeedback();
    if (replaceFileInputRef.current) {
      replaceFileInputRef.current.click();
    }
  }

  async function handleReplaceFileSelection(event) {
    const files = event.target.files;
    if (!files || files.length === 0 || !replacingResource) return;

    const nextFile = files[0];
    const errors = validateFiles([nextFile]);
    if (errors.length > 0) {
      setValidationErrors(errors);
      return;
    }

    const scope = replacingResource.assignmentId ? "assignment" : "general";
    await uploadFiles(scope, [nextFile]);
  }

  function startRename(resource) {
    setRenamingResourceId(resource.resourceId);
    setRenameValue(resource.originalFilename || "");
  }

  function cancelRename() {
    setRenamingResourceId(null);
    setRenameValue("");
  }

  async function saveRename(resourceId) {
    resetFeedback();
    try {
      await APIService.UpdateCourseResource(resourceId, { originalFilename: renameValue });
      setUploadMessage("File name updated.");
      cancelRename();
      await loadResources(selectedCourseId, selectedAssignmentId);
    } catch (error) {
      setUploadError(error.message || "Failed to rename file.");
    }
  }

  function beginEditAssignment(assignment) {
    setEditingAssignmentId(assignment.assignmentId);
    setAssignmentForm({
      title: assignment.title || "",
      description: assignment.description || "",
      assignmentType: assignment.assignmentType || "assignment",
      assignmentNumber: assignment.assignmentNumber ?? "",
      isActive: assignment.isActive !== false,
    });
  }

  function resetAssignmentForm() {
    setEditingAssignmentId(null);
    setAssignmentForm(EMPTY_ASSIGNMENT_FORM);
  }

  async function handleAssignmentSubmit(event) {
    event.preventDefault();
    if (!selectedCourseId) {
      setUploadError("Choose a course first.");
      return;
    }

    resetFeedback();

    try {
      if (editingAssignmentId) {
        await APIService.UpdateAssignment(editingAssignmentId, assignmentForm);
        setUploadMessage("Assignment updated.");
      } else {
        await APIService.CreateAssignment(selectedCourseId, assignmentForm);
        setUploadMessage("Assignment created.");
      }
      resetAssignmentForm();
      await loadAssignments(selectedCourseId);
      await loadResources(selectedCourseId, selectedAssignmentId);
    } catch (error) {
      setUploadError(error.message || "Unable to save assignment.");
    }
  }

  async function handleArchiveAssignment(assignmentId) {
    resetFeedback();
    try {
      await APIService.DeleteAssignment(assignmentId);
      setUploadMessage("Assignment archived.");
      if (editingAssignmentId === assignmentId) {
        resetAssignmentForm();
      }
      await loadAssignments(selectedCourseId);
      await loadResources(selectedCourseId, selectedAssignmentId);
    } catch (error) {
      setUploadError(error.message || "Unable to archive assignment.");
    }
  }

  if (checkingAuth || !authorized) {
    return null;
  }

  return (
    <FacultyDashboardShell
      title="Content"
      description="Manage course material and assignment material."
      message={uploadMessage}
      error={uploadError}
      workspaceCourse={selectedCourse}
      workspaceMode={selectedCourse ? "selected_course" : "all_courses"}
      showWorkspaceBanner={false}
    >
      <main id="main-content" className="faculty-content-workspace">
        {!selectedCourse && (
          <div className="faculty-card faculty-empty-state-card">
            <div className="faculty-empty-state">Choose a course first.</div>
          </div>
        )}

        {selectedCourse && (
          <>
            <section className="faculty-card faculty-content-header">
              <div>
                <div className="faculty-eyebrow">Selected Course</div>
                <h1>{selectedCourse.courseId}</h1>
                <p>
                  {selectedCourse.name || "Untitled course"}
                  {(selectedCourse.section || selectedCourse.semester) && ` · ${[selectedCourse.section, selectedCourse.semester].filter(Boolean).join(" · ")}`}
                </p>
              </div>
              <div className="faculty-page-actions">
                <button className="btn btn-outline-secondary faculty-secondary-button" type="button" onClick={() => navigate("/dashboard")}>
                  Edit Details
                </button>
              </div>
            </section>

            <section className="faculty-content-columns">
              <section className="faculty-card faculty-material-column" aria-label="Course material">
                <div className="faculty-section-heading">
                  <h2>Course Material</h2>
                </div>

                <div className="faculty-upload-controls">
                  <label>
                    <span>Type</span>
                    <select className="form-select" value={generalDocumentType} onChange={(event) => setGeneralDocumentType(event.target.value)}>
                      <option value="syllabus">Syllabus</option>
                      <option value="policy">Policy</option>
                      <option value="notes">Notes</option>
                      <option value="study-guide">Study Guide</option>
                      <option value="assignment">Assignment</option>
                    </select>
                  </label>
                  <div className="input-group">
                    <input
                      ref={generalFileInputRef}
                      type="file"
                      className={`form-control ${validationErrors.length > 0 ? "is-invalid" : ""}`}
                      accept={ALLOWED_EXT_LABEL}
                      multiple
                      onChange={(event) => handleFileChange("general", event)}
                      disabled={uploadingScope === "general"}
                    />
                    <button className="btn btn-create-course" type="button" onClick={() => uploadFiles("general")} disabled={uploadingScope === "general" || generalFiles.length === 0}>
                      {uploadingScope === "general" ? "Uploading..." : "Upload Files"}
                    </button>
                  </div>
                </div>

                <small className="text-muted d-block mt-2">General course material is used when no assignment is selected.</small>

                <div className="faculty-resource-list faculty-resource-list-compact">
                  {generalResources.map((resource) => (
                    <div key={resource.resourceId} className="faculty-resource-item">
                      <div className="faculty-resource-row">
                        <div className="faculty-resource-copy">
                          {renamingResourceId === resource.resourceId ? (
                            <div className="faculty-inline-rename">
                              <input className="form-control" value={renameValue} onChange={(event) => setRenameValue(event.target.value)} />
                              <div className="faculty-inline-actions">
                                <button className="btn btn-sm btn-outline-secondary" type="button" onClick={() => saveRename(resource.resourceId)}>
                                  Save
                                </button>
                                <button className="btn btn-sm btn-outline-secondary" type="button" onClick={cancelRename}>
                                  Cancel
                                </button>
                              </div>
                            </div>
                          ) : (
                            <>
                              <strong>{resource.originalFilename}</strong>
                              <span>{resource.documentType || resource.resource_type || "Document"}</span>
                            </>
                          )}
                        </div>
                        <div className="faculty-inline-actions">
                          <button className="btn btn-outline-secondary btn-sm" type="button" onClick={() => startRename(resource)}>
                            Rename
                          </button>
                          <button className="btn btn-outline-secondary btn-sm" type="button" onClick={() => handleReplaceIntent(resource)}>
                            Replace
                          </button>
                          <button className="btn btn-outline-danger btn-sm" type="button" onClick={() => handleDeleteResource(resource.resourceId)}>
                            Delete
                          </button>
                        </div>
                      </div>
                      <details className="faculty-resource-preview">
                        <summary onClick={() => setPreviewResourceId(resource.resourceId)}>Preview extracted content</summary>
                        <div className="faculty-resource-preview-body">
                          <div className="faculty-resource-preview-copy">{resource.previewText || "No preview is available for this file yet."}</div>
                        </div>
                      </details>
                    </div>
                  ))}
                  {generalResources.length === 0 && <div className="faculty-empty-state">No course material yet.</div>}
                </div>
              </section>

              <section className="faculty-card faculty-material-column" aria-label="Assignment material">
                <div className="faculty-section-heading">
                  <h2>Assignment Material</h2>
                  <button className="btn btn-outline-secondary faculty-secondary-button" type="button" onClick={resetAssignmentForm}>
                    Add Assignment
                  </button>
                </div>

                <form className="faculty-form-grid faculty-form-grid-compact faculty-assignment-editor" onSubmit={handleAssignmentSubmit}>
                  <label>
                    <span>Title</span>
                    <input
                      className="form-control"
                      value={assignmentForm.title}
                      onChange={(event) => setAssignmentForm({ ...assignmentForm, title: event.target.value })}
                      required
                    />
                  </label>
                  <label>
                    <span>Type</span>
                    <select
                      className="form-select"
                      value={assignmentForm.assignmentType}
                      onChange={(event) => setAssignmentForm({ ...assignmentForm, assignmentType: event.target.value })}
                    >
                      <option value="assignment">Assignment</option>
                      <option value="lab">Lab</option>
                      <option value="topic">Topic</option>
                      <option value="exam-review">Exam review</option>
                    </select>
                  </label>
                  <label>
                    <span>Number</span>
                    <input
                      className="form-control"
                      value={assignmentForm.assignmentNumber}
                      onChange={(event) => setAssignmentForm({ ...assignmentForm, assignmentNumber: event.target.value })}
                      placeholder="Optional"
                    />
                  </label>
                  <label className="faculty-form-span">
                    <span>Description</span>
                    <textarea
                      className="form-control"
                      rows="2"
                      value={assignmentForm.description}
                      onChange={(event) => setAssignmentForm({ ...assignmentForm, description: event.target.value })}
                    />
                  </label>
                  <div className="faculty-inline-actions">
                    <button className="btn btn-create-course" type="submit">
                      {editingAssignmentId ? "Save Assignment" : "Add Assignment"}
                    </button>
                    {editingAssignmentId && (
                      <button className="btn btn-outline-secondary faculty-secondary-button" type="button" onClick={resetAssignmentForm}>
                        Cancel
                      </button>
                    )}
                  </div>
                </form>

                <div className="faculty-assignment-list faculty-assignment-list-selectable">
                  {assignments.map((assignment) => (
                    <button
                      key={assignment.assignmentId}
                      type="button"
                      className={`faculty-assignment-item ${String(assignment.assignmentId) === String(selectedAssignmentId) ? "active" : ""}`}
                      onClick={() => setSelectedAssignmentId(String(assignment.assignmentId))}
                    >
                      <div>
                        <strong>
                          {assignment.assignmentNumber ? `#${assignment.assignmentNumber} ` : ""}
                          {assignment.title}
                        </strong>
                        <span>{assignment.assignmentType}</span>
                      </div>
                      <div className="faculty-inline-actions">
                        <button className="btn btn-outline-secondary btn-sm" type="button" onClick={(event) => { event.stopPropagation(); beginEditAssignment(assignment); }}>
                          Edit
                        </button>
                        <button className="btn btn-outline-danger btn-sm faculty-subtle-action" type="button" onClick={(event) => { event.stopPropagation(); handleArchiveAssignment(assignment.assignmentId); }}>
                          Archive
                        </button>
                      </div>
                    </button>
                  ))}
                  {assignments.length === 0 && <div className="faculty-empty-state">No assignments yet.</div>}
                </div>

                {selectedAssignment ? (
                  <>
                    <div className="faculty-upload-controls">
                      <label>
                        <span>Type</span>
                        <select className="form-select" value={assignmentDocumentType} onChange={(event) => setAssignmentDocumentType(event.target.value)}>
                          <option value="assignment">Assignment</option>
                          <option value="notes">Notes</option>
                          <option value="study-guide">Study Guide</option>
                          <option value="policy">Policy</option>
                        </select>
                      </label>
                      <div className="input-group">
                        <input
                          ref={assignmentFileInputRef}
                          type="file"
                          className={`form-control ${validationErrors.length > 0 ? "is-invalid" : ""}`}
                          accept={ALLOWED_EXT_LABEL}
                          multiple
                          onChange={(event) => handleFileChange("assignment", event)}
                          disabled={uploadingScope === "assignment"}
                        />
                        <button className="btn btn-create-course" type="button" onClick={() => uploadFiles("assignment")} disabled={uploadingScope === "assignment" || assignmentFiles.length === 0}>
                          {uploadingScope === "assignment" ? "Uploading..." : "Upload Files"}
                        </button>
                      </div>
                    </div>

                    <div className="faculty-assignment-material-header">
                      <strong>{selectedAssignment.title}</strong>
                      <span>{selectedAssignment.description || "Selected assignment"}</span>
                    </div>

                    <div className="faculty-resource-list faculty-resource-list-compact">
                      {assignmentResources.map((resource) => (
                        <div key={resource.resourceId} className="faculty-resource-item">
                          <div className="faculty-resource-row">
                            <div className="faculty-resource-copy">
                              {renamingResourceId === resource.resourceId ? (
                                <div className="faculty-inline-rename">
                                  <input className="form-control" value={renameValue} onChange={(event) => setRenameValue(event.target.value)} />
                                  <div className="faculty-inline-actions">
                                    <button className="btn btn-sm btn-outline-secondary" type="button" onClick={() => saveRename(resource.resourceId)}>
                                      Save
                                    </button>
                                    <button className="btn btn-sm btn-outline-secondary" type="button" onClick={cancelRename}>
                                      Cancel
                                    </button>
                                  </div>
                                </div>
                              ) : (
                                <>
                                  <strong>{resource.originalFilename}</strong>
                                  <span>{resource.documentType || resource.resource_type || "Document"}</span>
                                </>
                              )}
                            </div>
                            <div className="faculty-inline-actions">
                              <button className="btn btn-outline-secondary btn-sm" type="button" onClick={() => startRename(resource)}>
                                Rename
                              </button>
                              <button className="btn btn-outline-secondary btn-sm" type="button" onClick={() => handleReplaceIntent(resource)}>
                                Replace
                              </button>
                              <button className="btn btn-outline-danger btn-sm" type="button" onClick={() => handleDeleteResource(resource.resourceId)}>
                                Delete
                              </button>
                            </div>
                          </div>
                          <details className="faculty-resource-preview">
                            <summary onClick={() => setPreviewResourceId(resource.resourceId)}>Preview extracted content</summary>
                            <div className="faculty-resource-preview-body">
                              <div className="faculty-resource-preview-copy">{resource.previewText || "No preview is available for this file yet."}</div>
                            </div>
                          </details>
                        </div>
                      ))}
                      {assignmentResources.length === 0 && <div className="faculty-empty-state">No assignment material yet.</div>}
                    </div>
                  </>
                ) : (
                  <div className="faculty-empty-state">Choose an assignment to manage assignment material.</div>
                )}
              </section>
            </section>

            {previewResource && (
              <section className="faculty-card faculty-preview-footer">
                <div className="faculty-section-heading">
                  <h2>Preview extracted content</h2>
                </div>
                <div className="faculty-resource-preview-copy">{previewResource.previewText || "No preview is available for this file yet."}</div>
              </section>
            )}
          </>
        )}

        <input
          ref={replaceFileInputRef}
          type="file"
          accept={ALLOWED_EXT_LABEL}
          className="d-none"
          onChange={handleReplaceFileSelection}
          aria-label="Choose replacement file"
        />
      </main>
    </FacultyDashboardShell>
  );
}

export default AddCourse;
